// ViewerToolbar — the one top bar for long-form viewers (blog, newsletter):
// segmented tabs on the left; on the right "Änderungen speichern" (only while
// there is something to save) and a single "Exportieren" menu.

import { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, Loader2, Share } from 'lucide-react';

export default function ViewerToolbar({ tabs, active, onTab, dirty = false, saving = false, onSave, saveLabel = 'Änderungen speichern', exportItems = [], exportLabel = 'Exportieren', testIdPrefix = 'viewer' }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const run = async (item) => {
    setOpen(false);
    try {
      const r = await item.onClick();
      if (item.doneLabel && r !== false) { setDone(item.doneLabel); setTimeout(() => setDone(''), 1800); }
    } catch (_) { /* item shows its own errors */ }
  };

  return (
    <div className="no-print flex flex-wrap items-center justify-between gap-3" data-testid={`${testIdPrefix}-toolbar`}>
      <div className="inline-flex gap-1 rounded-lg bg-[#1A2B42] p-1" role="tablist">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={active === t.key} onClick={() => onTab(t.key)}
            className={`rounded-md px-4 py-2 text-sm transition ${active === t.key ? 'bg-[#00D9C8] font-medium text-[#0A1628]' : 'text-[#A0AEC0] hover:text-white'}`}
            data-testid={`${testIdPrefix}-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {dirty && onSave && (
          <button type="button" onClick={onSave} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#00D9C8] px-3 py-2 text-sm font-medium text-[#0A1628] hover:bg-[#00D9C8]/90 disabled:opacity-60"
            data-testid={`${testIdPrefix}-save`}>
            {saving && <Loader2 size={14} className="animate-spin" />} {saveLabel}
          </button>
        )}
        {exportItems.length > 0 && (
          <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
              className="inline-flex items-center gap-2 rounded-md border border-white/15 px-3 py-2 text-sm text-white hover:bg-white/5"
              data-testid={`${testIdPrefix}-export`}>
              {done ? <Check size={14} /> : <Share size={14} />} {done || exportLabel} <ChevronDown size={14} />
            </button>
            {open && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-white/10 bg-[#1A2B42] shadow-xl">
                {exportItems.map((item) => (
                  <button key={item.label} type="button" role="menuitem" onClick={() => run(item)}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-white hover:bg-white/5"
                    data-testid={item.testId}>
                    {item.icon} {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
